import { SWRFetcher } from '@commenda/utils';
import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import {
  CommendaService,
  CommendaServicesResponse,
  UsePaginatedCommendaServicesParams,
} from './types';

const PAGE_SIZE = 25;

function buildKey(
  search: string,
  category: string | undefined,
  subcategory: string | undefined,
  limit: number,
  offset: number
) {
  const qs = new URLSearchParams();
  qs.set('limit', String(limit));
  qs.set('offset', String(offset));
  if (search) qs.set('query', search);
  if (category) qs.set('category', category);
  if (subcategory) qs.set('subcategory', subcategory);
  return `commenda-services?${qs}`;
}

export function usePaginatedCommendaServices({
  search = '',
  category,
  subcategory,
  pageSize = PAGE_SIZE,
}: UsePaginatedCommendaServicesParams = {}) {
  const trimmedSearch = search.trim();

  const getKey = (
    pageIndex: number,
    prevPage: CommendaServicesResponse | null
  ): string | null => {
    if (prevPage && pageIndex * pageSize >= prevPage.total) return null;
    return buildKey(
      trimmedSearch,
      category,
      subcategory,
      pageSize,
      pageIndex * pageSize
    );
  };

  const { data, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite<CommendaServicesResponse>(getKey, SWRFetcher({ method: 'GET' }), {
      revalidateFirstPage: false,
      keepPreviousData: true,
    });

  const items = useMemo<CommendaService[]>(
    () => (data ? data.flatMap((page) => page?.items ?? []) : []),
    [data]
  );

  const total = data?.[0]?.total ?? 0;
  const hasMore = items.length < total;
  // SWRInfinite leaves the slot for an in-flight page undefined until it resolves.
  const isLoadingMore = !!data && typeof data[size - 1] === 'undefined';

  const loadMore = () => {
    if (hasMore && !isLoadingMore) {
      setSize(size + 1);
    }
  };

  return {
    items,
    hasMore,
    loadMore,
    isLoading: !!isLoading,
    isLoadingMore,
    isValidating: !!isValidating,
    mutate,
  };
}
